import { Link, useLocation, useNavigate } from "react-router-dom";
import { Layout, Menu, Badge, Skeleton, Button } from "antd";
import smallIcon from "../../../assets/images/travel-icon.jpg";
import "./SideMenu.less";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "../../../hooks/auth/AuthContext";
import { useIntl } from "react-intl";
import {
  CloseOutlined,
  DashboardOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  MenuOutlined,
  QuestionOutlined,
  SettingOutlined,
  UserOutlined,
  LockOutlined,
  ApartmentOutlined,
  WalletOutlined,
  TeamOutlined,
  BarChartOutlined,
  DollarCircleOutlined,
  UsergroupAddOutlined,
  PartitionOutlined,
  SafetyOutlined,
  SecurityScanOutlined,
  KeyOutlined,
} from "@ant-design/icons";
const { Sider } = Layout;

const SideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const [selectedKey, setSelectedKey] = useState<string>(location.pathname);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [subMenuKey, setSubMenuKey] = useState<string>();
  const { authState } = useAuth();
  const [sideNavs, setSideNavs] = useState<any[]>();

  useEffect(() => {
    const selectedKeys = location.pathname.split("/");
    setSelectedKey(location.pathname);
    const expandedSubMenu =
      selectedKeys.length > 2 ? `/${selectedKeys[1]}` : undefined;
    setSubMenuKey(collapsed ? undefined : expandedSubMenu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, collapsed]);

  useEffect(() => {
    if (authState.sideNav && authState.sideNav.length > 0) {
      // Filter out navigation items with 'NONE' role type (no access)
      const accessibleSideNavs = authState.sideNav.filter((sideNav: any) => 
        sideNav.roleType && sideNav.roleType !== 'NONE'
      );
      
      const sideNavs = accessibleSideNavs.map((sideNav: any) => ({
        ...sideNav,
        name: sideNav.name.replace(/[\s]/g, "_").toUpperCase()
      }));
      const sortedData = [...sideNavs].sort((a: any, b: any) =>
        handleSort(a, b)
      );
      const parentSideNavs: any[] = sortedData.filter((sn) => !sn.parentId);

      const nestedSideNav: any[] = parentSideNavs.map((sn) => {
        const parentSideNav = { ...sn };
        // Also filter child items to ensure no 'NONE' access children are included
        parentSideNav.child = sortedData.filter((c) => 
          c.parentId === sn.id && c.roleType !== 'NONE'
        );
        return parentSideNav;
      });
      setSideNavs(nestedSideNav);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.sideNav]);

  const handleCollapsed = useCallback(() => {
    if (windowWidth >= 768) {
      setIsMobile(false);
      setCollapsed(false);
    } else {
      setIsMobile(true);
      setCollapsed(true);
    }
  }, [windowWidth]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Call once on mount to set initial state
    handleCollapsed();

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleCollapsed]);

  // Handle responsive behavior when window width changes
  useEffect(() => {
    handleCollapsed();
  }, [windowWidth, handleCollapsed]);

  useEffect(() => {
    if (isMobile && !collapsed) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobile, collapsed]);

  const handleSort = (a: any, b: any): number => {
    if (a.order > b.order) return 1;
    else if (a.order < b.order) return -1;
    return 0;
  };

  const handleSubMenuClick = (e: any) => {
    if (!e || (e && e.length === 0)) {
      setSubMenuKey(undefined);
    } else if (e && e.length > 1) {
      setSubMenuKey(e[1]);
    } else if (e && e.length > 0) {
      setSubMenuKey(e[0]);
    }
  };

  const getIcon = useCallback((iconName: string): any => {
     switch(iconName) {
      case "DashboardOutlined":
      case "dashboard":
        return <DashboardOutlined />;
      case "ApartmentOutlined":
      case "level-tree":
      case "apartment":
        return <ApartmentOutlined />;
      case "WalletOutlined":
      case "wallet":
        return <WalletOutlined />;
      case "TeamOutlined":
      case "team":
        return <TeamOutlined />;
      case "BarChartOutlined":
      case "reports":
      case "chart":
        return <BarChartOutlined />;
      case "DollarCircleOutlined":
      case "income":
      case "dollar":
        return <DollarCircleOutlined />;
      case "UsergroupAddOutlined":
      case "usergroup":
      case "team-report":
        return <UsergroupAddOutlined />;
      case "SettingOutlined":
      case "settings":
        return <SettingOutlined />;
      case "UserOutlined":
      case "users":
        return <UserOutlined />;
      case "Network":
      case "network":
        return <PartitionOutlined />;
      case "LockOutlined":
      case "shield": //role
        return <LockOutlined />;
      case "SafetyOutlined":
      case "security": //role & permissions
      case "role-permission":
      case "permissions":
        return <SafetyOutlined />;
      case "SecurityScanOutlined":
      case "role-shield":
      case "scan":
        return <SecurityScanOutlined />;
      case "KeyOutlined":
      case "keys":
      case "access":
        return <KeyOutlined />;
      case "user-add":
        return <UsergroupAddOutlined />;
      default:
        return <QuestionOutlined />;
     }
    }, []);
  // const subMenus: any = sideNavs?.map((sideNav, index) => {
  //     const _tempContent: any = { key: `sm-dd-${index + 1}`, icon: getIcon(sideNav.icon) };
  //     return sideNav.child ? {
  //         ..._tempContent, label: intl.formatMessage({ id: sideNav.name }), children: sideNav.child.map((child: any, i: number) => {
  //             return { key: `sm-dd-${index + 1}-${i}`, label: <><Link to={child.route}>{intl.formatMessage({ id: child.name })}</Link></>, };
  //         })
  //     } : { ..._tempContent, label: <><Link to={sideNav.route}>{intl.formatMessage({ id: sideNav.name })}</Link></>, };

  // }) ?? [];
  // let items: any = [
  //     { key: 'sm-dd-0', icon: <DashboardOutlined />, label: <><Link to="/">{intl.formatMessage({ id: "DASHBOARD" })}</Link></> },
  //     ...subMenus
  // ];

  const handleMenuItemClick = useCallback(() => {
    // Hide menu on mobile when item is clicked
    if (isMobile && !collapsed) {
      setCollapsed(true);
    }
  }, [isMobile, collapsed]);

  const menuItems = useMemo(() => {
    if (!sideNavs) return [];

    return sideNavs.map((sideNav) => {
      const indicator =
        authState &&
        authState?.indicatorList &&
        authState.indicatorList.length > 0
          ? authState?.indicatorList?.filter(
              (u: { parentId: any; isShowIndicator: boolean }) =>
                u.parentId === sideNav.id && u.isShowIndicator === true
            )
          : [];
      const isShowIndicator = indicator && indicator.length > 0;

      if (sideNav.child.length > 0) {
        // SubMenu with children
        return {
          key: sideNav.route,
          icon: getIcon(sideNav.icon),
          label: (
            <>
              {intl.formatMessage({ id: sideNav.name })}
              <Badge dot={isShowIndicator} style={{ marginLeft: 5 }} />
            </>
          ),
          children: sideNav.child.map((child: any) => {
            const indicatorCount =
              authState?.indicatorList?.find(
                (u: { id: any }) => u.id === child.id
              )?.indicatorCount || 0;
            return {
              key: child.route,
              label: (
                <Link to={child.route} onClick={handleMenuItemClick}>
                  {intl.formatMessage({ id: child.name })}
                  <Badge
                    count={indicatorCount}
                    style={{ marginBottom: 5, marginLeft: 2 }}
                  />
                </Link>
              ),
            };
          }),
        };
      } else {
        // Regular menu item
        return {
          key: sideNav.route,
          icon: getIcon(sideNav.icon),
          label: (
            <Link to={sideNav.route} onClick={handleMenuItemClick}>
              {intl.formatMessage({ id: sideNav.name })}
            </Link>
          ),
        };
      }
    });
  }, [sideNavs, intl, authState, getIcon, handleMenuItemClick]);

  const renderMenu = () => {
    return sideNavs ? (
      <Menu
        theme="dark"
        mode="inline"
        triggerSubMenuAction={collapsed ? "hover" : "click"}
        onOpenChange={handleSubMenuClick}
        selectedKeys={[selectedKey ? selectedKey : "/"]}
        openKeys={subMenuKey ? [subMenuKey] : undefined}
        items={menuItems}
      />
    ) : (
      <div style={{ margin: "30px 15px" }}>
        <Skeleton active />
      </div>
    );
  };

  return (
    <>
      {/* Mobile Menu Button - Always visible on mobile when sidebar is collapsed */}
      {isMobile && collapsed && (
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setCollapsed(!collapsed)}
          className="side-nav-button-mobile"
        />
      )}
      
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        collapsedWidth={isMobile ? 0 : 64}
        width={256}
        style={{ zIndex: 1000 }}  
      >
        <div className={!collapsed ? "side-nav-logo" : "side-nav-logo collapsed"}>
          <div className="side-nav-header">
            <img
              src={smallIcon}
              alt="small-icon"
              className="side-nav-logo-icon"
              onClick={() => navigate("/dashboard")}
            />
            {isMobile && !collapsed && (
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                className="side-nav-button-mobile-close"
              />
            )}
          </div>
          {!isMobile && (
            <Button
              type="text"
              icon={collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className={
                collapsed ? "side-nav-button-collapsed" : "side-nav-button"
              }
            />
          )}
        </div>
        {renderMenu()}
      </Sider>
    </>
  );
};

export default SideMenu;
